// Shared org-type vocabulary for filtering (list page + Explore + labels).
// OrganizationType enum: SCHOOL | CLUB_TEAM (legacy) | CLUB | LEAGUE | BAND.

import type { ListEntityKind } from '../types/list';

export interface OrgTypeFilterOption {
  value: string;       // canonical value used by the chip
  label: string;       // rep-facing label
  matchValues: string[]; // enum values this option filters on (folds legacy CLUB_TEAM under CLUB)
}

export const ORG_TYPE_FILTER_OPTIONS: OrgTypeFilterOption[] = [
  { value: 'SCHOOL', label: 'School', matchValues: ['SCHOOL'] },
  { value: 'CLUB', label: 'Club', matchValues: ['CLUB', 'CLUB_TEAM'] },
  { value: 'LEAGUE', label: 'League', matchValues: ['LEAGUE'] },
  { value: 'BAND', label: 'Band', matchValues: ['BAND'] },
];

// Single-value {value,label} list for the generic Explore enum facet.
export const ORG_TYPE_SELECT_OPTIONS = ORG_TYPE_FILTER_OPTIONS.map(({ value, label }) => ({ value, label }));

export function orgTypeLabel(type: string | null | undefined): string {
  switch (type) {
    case 'SCHOOL': return 'School';
    case 'CLUB':
    case 'CLUB_TEAM': return 'Club';
    case 'LEAGUE': return 'League';
    case 'BAND': return 'Band';
    default: return type ?? '';
  }
}

/** The Blast id for an org, by type: SCHOOL→blastSchoolId, CLUB/CLUB_TEAM/LEAGUE→blastClubId, BAND→blastBandId. */
export function blastEntityId(org: {
  type: string | null;
  blastSchoolId: string | null;
  blastClubId: string | null;
  blastBandId: string | null;
}): string | null {
  switch (org.type) {
    case 'SCHOOL': return org.blastSchoolId;
    case 'CLUB':
    case 'CLUB_TEAM':
    case 'LEAGUE': return org.blastClubId;
    case 'BAND': return org.blastBandId;
    default: return org.blastSchoolId ?? org.blastClubId ?? org.blastBandId;
  }
}

/** Map an OrganizationType to the nearest ListEntityKind the backend supports
 *  for list membership (SCHOOL stays SCHOOL; CLUB/LEAGUE/BAND/CLUB_TEAM fold to
 *  CLUB_TEAM — the backend ListEntityKind enum has no CLUB/LEAGUE/BAND member). */
export function orgTypeToListKind(type: string): ListEntityKind {
  return type === 'SCHOOL' ? 'SCHOOL' : 'CLUB_TEAM';
}

const STORAGE_KEY = 'crm.orgList.orgTypes';

export function readPersistedOrgTypes(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function writePersistedOrgTypes(values: string[]): void {
  try {
    if (values.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore quota / private-mode errors
  }
}
